import { useTranslation } from "react-i18next";
import { cn, Icon } from "@nexus/ui";
import type { ChangelogDelivery } from "@/features/changelog/api/changelog.api";
import { CHANGELOG_CHANNEL_ORDER } from "@/features/changelog/lib/changelog-display";
import { CHANNEL_ICON } from "./ChangelogDeliveryList";

const STATUS_COLOR: Record<ChangelogDelivery["status"], string> = {
  PENDING: "--muted-foreground",
  SENDING: "--muted-foreground",
  SENT: "--success",
  FAILED: "--error",
};

/**
 * Delivery at a glance: one mark per channel that was asked for, coloured by
 * outcome. The reasons and the retry buttons live in the detail sheet — a row
 * only needs to be scannable enough to decide whether to open it.
 */
export function ChangelogDeliveryCell({ deliveries }: { deliveries: ChangelogDelivery[] }) {
  const { t } = useTranslation();

  const ordered = CHANGELOG_CHANNEL_ORDER.map((channel) =>
    deliveries.find((delivery) => delivery.channel === channel),
  ).filter((delivery): delivery is ChangelogDelivery => delivery !== undefined);

  if (ordered.length === 0) {
    return <span className="text-muted-foreground">{t("admin.changelog.table.notSent", "—")}</span>;
  }

  return (
    <span className="flex items-center gap-2">
      {ordered.map((delivery) => {
        const channel = t(`admin.changelog.channels.${delivery.channel}`, {
          defaultValue: delivery.channel,
        });
        const status = delivery.isStuck
          ? t("admin.changelog.delivery.stuckShort", "Stalled")
          : t(`admin.changelog.delivery.status.${delivery.status}`, {
              defaultValue: delivery.status,
            });

        return (
          <span
            key={delivery.channel}
            className="inline-flex items-center gap-1"
            title={`${channel} · ${status}`}
          >
            <Icon name={CHANNEL_ICON[delivery.channel]} size="xs" color="muted" aria-hidden />
            <Icon
              name={delivery.isStuck ? "alert-circle" : DELIVERY_ICON[delivery.status]}
              size="xs"
              colorVar={delivery.isStuck ? "--warning" : STATUS_COLOR[delivery.status]}
              className={cn(delivery.status === "SENDING" && !delivery.isStuck && "animate-spin")}
              aria-hidden
            />
            <span className="sr-only">{`${channel} ${status}`}</span>
          </span>
        );
      })}
    </span>
  );
}

const DELIVERY_ICON = {
  PENDING: "clock",
  SENDING: "loader",
  SENT: "check-circle",
  FAILED: "x-circle",
} as const;
